import { Alert, AlertTitle } from "@mui/material";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTenantBranding } from "../hooks/useTenantBranding";
import { cacheTenantBranding } from "../utils/tenantBrandingCache";
import styles from "./AuthPage.module.css";
import ForgotPasswordForm from "./ForgotPasswordForm";
import LoadingSpinner from "./LoadingSpinner";
import LoginForm from "./LoginForm";
import RegisterForm from "./RegisterForm";

type AuthMode = "login" | "register" | "forgot-password" | "check-email";

const AuthPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const [mode, setMode] = useState<AuthMode>("login");
  const [registeredEmail, setRegisteredEmail] = useState("");
  const {
    backgroundImage,
    error: tenantError,
    isLoading: tenantLoading,
    tenantInfo,
  } = useTenantBranding();

  // cache tenant branding data when successfully loaded
  useEffect(() => {
    if (tenantInfo && tenantSlug && !tenantError && tenantInfo.branding) {
      cacheTenantBranding(tenantSlug, tenantInfo.branding, tenantInfo.name);
    }
  }, [tenantInfo, tenantSlug, tenantError]);

  // set mode based on the route, but only allow registration with valid tenant slug
  useEffect(() => {
    if (
      (location.pathname === "/register" ||
        location.pathname.endsWith("/register")) &&
      tenantSlug &&
      !tenantError &&
      !tenantLoading
    ) {
      setMode("register");
    } else if (
      location.pathname === "/reset-password" ||
      location.pathname.endsWith("/reset-password")
    ) {
      setMode("forgot-password");
    } else if (
      location.pathname === "/login" ||
      location.pathname.endsWith("/login")
    ) {
      setMode("login");
    }
  }, [location.pathname, tenantSlug, tenantError, tenantLoading]);

  // function to switch between login/register modes with URL navigation
  const switchMode = (newMode: "login" | "register") => {
    // update local state immediately for responsive UI
    setMode(newMode);

    if (tenantSlug) {
      // navigate to tenant-specific routes
      navigate(`/${tenantSlug}/${newMode}`);
    } else {
      // navigate to global login (register not available without tenant)
      navigate("/login");
    }
  };

  // show loading state while validating tenant
  if (tenantLoading && tenantSlug) {
    return (
      <div className={styles.authContainer}>
        <div className={styles.authCard}>
          <LoadingSpinner minHeight="100px" />
        </div>
      </div>
    );
  }

  // show error for invalid tenant slug on registration routes
  if (tenantError && tenantSlug && location.pathname.endsWith("/register")) {
    return (
      <div className={styles.authContainer}>
        <div className={styles.authCard}>
          <div style={{ textAlign: "center", padding: "2rem" }}>
            <h2 className={styles.title}>Tenant Not Found</h2>
            <p className={styles.subtitle}>
              The tenant "{tenantSlug}" does not exist.
            </p>
            <p className={styles.subtitle}>
              Please check the URL or contact the business owner for the correct
              registration link.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const renderForm = () => {
    switch (mode) {
      case "login":
        return (
          <LoginForm onForgotPassword={() => setMode("forgot-password")} />
        );
      case "register":
        return (
          <RegisterForm
            onRegistrationSuccess={(email) => {
              setRegisteredEmail(email);
              setMode("check-email");
            }}
          />
        );
      case "forgot-password":
        return <ForgotPasswordForm onBackToLogin={() => switchMode("login")} />;
      case "check-email":
        return (
          <div>
            <h2 className={styles.title}>Check Your Email</h2>
            <p className={styles.subtitle}>
              We've sent a confirmation link to{" "}
              <strong>{registeredEmail}</strong>
            </p>
            <p className={styles.subtitle}>
              Click the link in your email to confirm your account and get
              started.
            </p>
            <div style={{ marginTop: "2rem", textAlign: "center" }}>
              <button
                type="button"
                onClick={() => switchMode("login")}
                className={styles.toggleBtn}
              >
                Back to Login
              </button>
            </div>
          </div>
        );
      default:
        return (
          <LoginForm onForgotPassword={() => setMode("forgot-password")} />
        );
    }
  };

  // create dynamic styles for background image (apply whenever we have a background image)
  const containerStyle = backgroundImage
    ? {
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }
    : {};

  return (
    <div className={styles.authContainer} style={containerStyle}>
      <div className={styles.authCard}>
        {renderForm()}

        {mode !== "forgot-password" && mode !== "check-email" && (
          <div style={{ marginTop: "1.5rem" }}>
            {mode !== "login" && (
              <p className={styles.toggleText}>Already have an account?</p>
            )}
            {(mode !== "login" || tenantSlug) && (
              <button
                type="button"
                onClick={() =>
                  switchMode(mode === "login" ? "register" : "login")
                }
                className={styles.toggleBtn}
                disabled={(() => {
                  const isLoginMode = mode === "login";
                  const hasTenantSlug = !!tenantSlug;
                  const hasTenantError = !!tenantError;

                  return isLoginMode && (!hasTenantSlug || hasTenantError);
                })()} // Disable register button if no tenant slug or tenant error
              >
                {mode === "login" ? "Create New Account" : "Sign In Instead"}
              </button>
            )}
            {mode === "login" && !tenantSlug && (
              <Alert
                severity="info"
                sx={{
                  mt: 2,
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                  color: "text.secondary",
                  border: "1px solid rgba(0, 0, 0, 0.12)",
                  "& .MuiAlert-icon": {
                    color: "text.secondary",
                  },
                }}
              >
                <AlertTitle sx={{ color: "text.primary" }}>
                  Account Registration
                </AlertTitle>
                To register contact the tenant owner.
              </Alert>
            )}
            {mode === "login" && tenantSlug && tenantError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                <AlertTitle>Tenant Not Found</AlertTitle>
                Cannot register: tenant "{tenantSlug}" not found
              </Alert>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthPage;
